import { useEffect, useState } from 'react';

import { getBrowserApiUrl } from '@/utils/apiEndpoint';
import { CODE_TERM } from '@/utils/termCodes';

import type Settings from '@/types/Settings';

import useCurrentLang from '../apps/useCurrentLang';
import { type TermsType, termsFeature } from './termsConfig';

type Props = {
  initialContent?: string;
  termsType: TermsType;
};

async function getSettingValue(settingKey: string, signal: AbortSignal) {
  const response = await fetch(
    getBrowserApiUrl(`settings/${encodeURIComponent(settingKey)}`),
    {
      credentials: 'include',
      headers: { Accept: 'application/json' },
      signal,
    },
  );
  if (!response.ok) {
    throw new Error(`Terms setting request failed with ${response.status}`);
  }

  const payload = (await response.json()) as { data?: Settings };
  return payload.data?.[settingKey]?.set_value ?? '';
}

async function loadTermsContent(
  settingKey: string,
  language: string,
  signal: AbortSignal,
) {
  const [content, replacements] = await Promise.all([
    getSettingValue(settingKey, signal),
    Promise.all(
      Object.entries(CODE_TERM).map(async ([key, code]) => ({
        code,
        value: await getSettingValue(`${key}${language}`, signal),
      })),
    ),
  ]);

  return replacements.reduce(
    (result, { code, value }) => result.replaceAll(code, value),
    content,
  );
}

export default function useGetTerms({ initialContent, termsType }: Props) {
  const currentLang = useCurrentLang();

  const [replaceContent, setReplacedContent] = useState(initialContent ?? '');

  const currentTerms = termsFeature[termsType];
  const currentKey = `${currentTerms?.setting_key ?? ''}${currentLang}`;

  useEffect(() => {
    if (initialContent !== undefined || !currentLang || !currentKey) return;

    const controller = new AbortController();
    void loadTermsContent(currentKey, currentLang, controller.signal)
      .then(setReplacedContent)
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        console.error('Failed to load terms content', error);
      });

    return () => controller.abort();
  }, [currentKey, currentLang, initialContent]);

  // 약관 모달 타이틀
  const modalTitle = currentTerms.label[currentLang];

  return { ...currentTerms, replaceContent, modalTitle };
}
